<template>
    <div class="container">
        <div class="title-container">
            <span>
                {{ $t('intervention.title') }} {{ this.$route.params.id }}
                <ReglePastille cle="interventionPreventive.definition" />
                <ReglePastille cle="suiviInterventionsPreventivesDetail.rapportDisponible" />
            </span>
        </div>
        <BorderedIconButton title="RETOUR" :imgUrl="imgUrl" className="back-btn" :onClick="this.$router.back" />
        <StatusTag :status="this.status" />
        <DataList v-if="this.interventionData && this.isLoaded"
                  :data="this.interventionData"
                  @select="goToPage" @downloadPdf="downloadPdf"/>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import DataList from '@/components/ui-components/DataList.vue';
import StatusTag, { statusName } from '@/components/ui-components/StatusTag.vue';
import BorderedIconButton from '@/components/ui-components/BorderedIconButton.vue';
import ErrorManager from '@/common/utils/ErrorManager';
import { ERROR } from '@/modules/ErrorToaster/store/mutation-types';
import SuiviInterventionPreventiveService
    from '@/modules/SuiviInterventionsPreventives/services/SuiviInterventionPreventiveService';
import router from '@/router';
import SuiviInterventionService from '@/modules/SuiviInterventions/services/SuiviInterventionService';
import DownloadPdf from '@/common/utils/DownloadPdf';
import StringUtils from '@/common/utils/StringUtils';
import ReglePastille from '@/components/regles/ReglePastille.vue';

type DataType = {
    interventionData: Array<any>;
    status: string | null;
    isLoaded: boolean;
    rapportTitre?: string;
};

export default defineComponent({
    name: 'InterventionPreventiveDetailPage',
    components: { DataList, StatusTag, BorderedIconButton, ReglePastille },
    data(): DataType {
        return {
            interventionData: [],
            isLoaded: false,
            status: null,
        };
    },
    beforeMount() {
        SuiviInterventionPreventiveService.getInterventionPreventive(+this.$route.params.id)
            .then((res: any) => {
                this.status = statusName(res.data.etat);
                this.interventionData = [
                    { label: this.$t('label.numIntervention'), value: res.data.id },
                    { label: this.$t('dataList.chantierLibelle'), value: res.data.adresseSite },
                    { label: this.$t('dataList.group'), value: res.data.groupeLibelle },
                    { label: this.$t('dataList.subgroup'), value: res.data.sousGroupeLibelle },
                    { label: this.$t('dataList.equipment'), value: res.data.equipement },
                    {
                        label: this.$t('dataList.predicatedDate'),
                        value: res.data.dateDebutPrevue ? new Date(res.data.dateDebutPrevue).toLocaleDateString() : '',
                    },
                    {
                        label: this.$t('dataList.dateDebutReelle'),
                        value: res.data.dateDebutReelle ? new Date(res.data.dateDebutReelle).toLocaleDateString() : '',
                    },
                    {
                        label: this.$t('dataList.dateRealise'),
                        value: res.data.dateFinReelle ? new Date(res.data.dateFinReelle).toLocaleDateString() : '',
                    },
                    { label: this.$t('dataList.type'), value: res.data.typeIntervention },
                    { label: this.$t('dataList.periodicity'), value: StringUtils.getPeriodiciteAsString(res.data.periodicite) },
                ];
                this.rapportTitre = res.data.id;

                this.isLoaded = true;
                return SuiviInterventionService.getRapportInfo(+this.$route.params.id);
            })
            .then(async res => {
                if (res.data.fichierExistant) {
                    this.interventionData.push({
                        label: this.$t('dataList.interventionReport'),
                        value: { pdfExist: true },
                    });
                } else {
                    this.interventionData.push({
                        label: this.$t('dataList.interventionReport'),
                        value: this.$t('dataList.interventionReportMissing'),
                    });
                }
            })
            .catch(async (err: any) => {
                const message = await ErrorManager.processError(err.response);

                this.$store.commit(`errorToaster/${ERROR.SET_ACTIVE}`, true);
                this.$store.commit(`errorToaster/${ERROR.SET_MESSAGE}`, message);
            });
    },
    computed: {
        imgUrl() {
            return require('@/assets/img/chevron-left-white.svg');
        },
    },
    methods: {
        goToPage(item: { id: { pageName: string; value: number; }; }){
            router.push({ name: item.id.pageName, params: { id: item.id.value } });
        },
        async downloadPdf() : Promise<void> {
            DownloadPdf.getInterventionPdf(+this.$route.params.id, 'Rapport_intervention_préventive-');
        }
    }
});
</script>

<style scoped>
.container {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 24px;
}
.title-container {
    color: var(--color-principal);
    font-size: var(--font-size-large);
    font-weight: var(--font-weight-600);
    margin-bottom: 15px;
}
</style>
